import { useEffect, useState, useMemo, useRef } from "react";
import { getStageBadgeClass, getStageFilterClass, STAGE_ORDER } from "../../utils/leadStageBadge";
import { usePersistedState } from "../../hooks/usePersistedState";
import { Button } from "../../components/ui/button";
import { Edit, Logs, Trash, RefreshCw, Search, ChevronDown, ChevronLeft, ChevronRight, Plus, Users, AlertCircle, Clock, CheckCircle, MapPin, ArrowRight, Phone, Handshake, AlertTriangle, Flame, RotateCcw, Star, Copy, Inbox, FilterX, X } from "lucide-react";
import { CUSTOMERCREATE, CUSTOMERSLIST, DEDUPE_CHECK_URL, DEDUPE_ATTACH_URL, USERSLIST_URL, CUSTOMER_TYPE_MASTER_URL, USERS_SIMPLE_URL, AI_CALLER_BUCKET_URL, CHANNEL_PARTNER_OPTIONS_URL, PROJECTS_URL, PROJECT_ASSIGNMENT_CONFIG_URL } from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";
import { ConfirmPopupModal, DialogModal } from "../../components/ui/dialogModal";
import { Input, Textarea } from "../../components/ui/input";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { ContainerCard } from "../../components/ui/cards";
import { Select } from "antd";
import { useNavigate } from "react-router-dom";
import LocationSelects from '../../components/common/LocationSelects';
import { DEFAULT_COUNTRY, stateName } from '../../utils/location';
import dayjs from "dayjs";
import { formatDate, formatTime } from "../../utils/datetime";
import DuplicateBadges from "../../components/customers/DuplicateBadges";
import ResearchMark from "../../components/customers/ResearchMark";
import LeadQualityBadges from "../../components/customers/LeadQualityBadges";
import DuplicateWarning from "../../components/customers/DuplicateWarning";
import OwnerHistory from "../../components/assignment/OwnerHistory";
import { AssignForm } from "./UnassignedLeads";
import { ASSIGNMENT_ASSIGN_URL, ASSIGNMENT_SETTINGS_URL } from "../../constants/api";

const DATE_PRESETS = [
    { value: "all",        label: "All Time" },
    { value: "today",      label: "Today" },
    { value: "yesterday",  label: "Yesterday" },
    { value: "last_week",  label: "Last 7 Days" },
    { value: "last_month", label: "Last 30 Days" },
    { value: "custom",     label: "Custom Range" },
];

const resolveDateRange = (preset, customFrom, customTo) => {
    const today = dayjs();
    const fmt   = d => d.format("YYYY-MM-DD");
    switch (preset) {
        case "today":      return { from_date: fmt(today),                        to_date: fmt(today) };
        case "yesterday":  return { from_date: fmt(today.subtract(1, "day")),     to_date: fmt(today.subtract(1, "day")) };
        case "last_week":  return { from_date: fmt(today.subtract(7,  "day")),    to_date: fmt(today) };
        case "last_month": return { from_date: fmt(today.subtract(30, "day")),    to_date: fmt(today) };
        case "custom":     return (customFrom && customTo) ? { from_date: customFrom, to_date: customTo } : {};
        default:           return {};
    }
};

const getDisplayLabel = (preset, customFrom, customTo) => {
    const today = dayjs();
    switch (preset) {
        case "today":      return today.format("DD MMM YYYY");
        case "yesterday":  return today.subtract(1, "day").format("DD MMM YYYY");
        case "last_week":  return `${today.subtract(7, "day").format("DD MMM")} – ${today.format("DD MMM YYYY")}`;
        case "last_month": return `${today.subtract(30, "day").format("DD MMM")} – ${today.format("DD MMM YYYY")}`;
        case "custom":     return (customFrom && customTo)
            ? `${dayjs(customFrom).format("DD MMM")} – ${dayjs(customTo).format("DD MMM YYYY")}` : null;
        default:           return null;
    }
};

// Must match CustomerDetails.CHANNEL_PARTNER_SOURCE on the backend: that is the
// single lead_source value the API accepts a partner alongside, and it blanks
// the attribution for any other source. A typo here would silently drop every
// partner a rep picked.
const CHANNEL_PARTNER_SOURCE = 'Channel Partner';

// Tolerant of the spellings years of imports left behind ('Channel Partner ',
// lower case): the backend compares the same way, so the picker must appear on
// exactly the leads whose attribution the API will accept.
const isPartnerSource = (v) => (v || '').trim().toLowerCase() === CHANNEL_PARTNER_SOURCE.toLowerCase();

// Quick views: work queues shown as tabs above the filters, one at a time.
// They are not stages — the stage pills below count within whichever view is
// open. 'needs_reassignment' is its own API flag; the rest are dup_filter
// values (trustedge customers/views.py _DUP_FILTERS; 'priority' = sources set
// in Settings -> Lead Priority).
const QUICK_VIEWS = [
    { key: 'needs_reassignment', label: 'Needs reassignment', icon: AlertTriangle, tone: 'text-amber-600 dark:text-amber-400',
      hint: 'Marked Unanswered too many times in a row (Settings → Lead Assignment). Hand these to another rep.',
      partnersToo: true },
    { key: 'priority', label: 'Priority', icon: Flame, tone: 'text-orange-600 dark:text-orange-400',
      hint: 'Open leads from priority sources (Settings → Lead Priority). Call these first.' },
    // The 7-day window lives in the hint, not the label: "Came back · 7d · 0"
    // read as a count of 7 that then opened an empty list.
    { key: 'came_back', label: 'Came back', icon: RotateCcw, tone: 'text-primary',
      hint: 'Enquired again in the last 7 days. Warm, call first.' },
    { key: 'existing_customer', label: 'Existing customer', icon: Star, tone: 'text-emerald-600 dark:text-emerald-400',
      hint: 'New leads on the phone number of a won or onboarded customer.' },
    { key: 'possible_duplicate', label: 'Same number', icon: Copy, tone: 'text-rose-600 dark:text-rose-400',
      hint: 'Open leads sharing a phone number with another lead. Open one to see the match and merge.' },
];

const selectCls = "appearance-none pl-3 pr-8 py-1.5 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none focus:ring-1 focus:ring-primary cursor-pointer";
// A dropdown with a value picked gets the brand outline, so active filters show at a glance.
const selectActiveCls = "border-primary/60 bg-primary/5 text-primary dark:text-primary";

const getFollowUpStatus = (dateStr) => {
    if (!dateStr) return null;
    const d    = dayjs(dateStr).startOf('day');
    const now  = dayjs().startOf('day');
    const diff = d.diff(now, 'day');
    if (diff < 0)   return { label: `${Math.abs(diff)}d overdue`, type: 'overdue' };
    if (diff === 0) return { label: 'Today',    type: 'today' };
    if (diff === 1) return { label: 'Tomorrow', type: 'upcoming' };
    return { label: `In ${diff}d`, type: 'upcoming' };
};


const CustomersList = ({ defaultCategory = 'prospect' }) => {
    const { create, fetch, remove } = useApiMethods();
    const navigate = useNavigate();
    const sessionStorageRole = sessionStorage.getItem('login_role') || '';

    // Use separate persisted-state keys so CP and Prospect pages don't share filters
    const prefix = defaultCategory === 'channel_partner' ? 'cp' : 'customers';

    // ── data state ──────────────────────────────────────────────────────
    const [data,          setData]          = useState([]);
    const [total,         setTotal]         = useState(0);
    // Separate from `total`: that one is the filtered count the pager runs on,
    // this one is the whole list, for the "All" badge.
    const [allCount,      setAllCount]      = useState(0);
    const [statusCount,   setStatusCount]   = useState({});
    const [loading,       setLoading]       = useState(true);

    // ── filter state ─────────────────────────────────────────────────────
    const [datePreset,     setDatePreset]     = usePersistedState(`${prefix}_date_preset`, "all");
    const [customFrom,     setCustomFrom]     = usePersistedState(`${prefix}_custom_from`, "");
    const [customTo,       setCustomTo]       = usePersistedState(`${prefix}_custom_to`,   "");
    const [selectedUserId, setSelectedUserId] = usePersistedState(`${prefix}_user`,        "");
    const customerCategory = defaultCategory;
    const [stageFilter,        setStageFilter]        = usePersistedState(`${prefix}_stage`,    "");
    const [needsReassignmentFilter, setNeedsReassignmentFilter] = usePersistedState(`${prefix}_needs_reassign`, false);
    // Duplicate chips: 'came_back' | 'existing_customer' | 'possible_duplicate' | ''
    const [dupFilter,          setDupFilter]          = usePersistedState(`${prefix}_dup`, "");
    const [dupCounts,          setDupCounts]          = useState({});
    const [needsReassignCount, setNeedsReassignCount] = useState(null);
    const [selectedCustomerType, setSelectedCustomerType] = usePersistedState(`${prefix}_ctype`,  "");
    const [partnerFilter,        setPartnerFilter]        = usePersistedState(`${prefix}_partner`, "");
    const [stateFilter,          setStateFilter]          = usePersistedState(`${prefix}_state`,   "");
    // [{state, count}] from the list API; '__blank__' is leads with no state.
    const [stateCounts,          setStateCounts]          = useState([]);
    const [customerName,         setCustomerName]         = usePersistedState("customers_cname",  "");
    const [cnInput,              setCnInput]              = useState(customerName);
    const [cnSuggestions,        setCnSuggestions]        = useState([]);
    const [cnOpen,               setCnOpen]               = useState(false);
    const cnRef                                           = useRef(null);
    const [search,               setSearch]               = useState("");

    // ── pagination ───────────────────────────────────────────────────────
    const [page,     setPage]     = useState(1);
    const [pageSize, setPageSize] = useState(25);

    // ── reference data ───────────────────────────────────────────────────
    const [repUsers,            setRepUsers]            = useState([]);
    const [UserList,            setUserList]            = useState([]);
    const [customerTypeOptions, setCustomerTypeOptions] = useState([]);
    const [allCustomerTypes,    setAllCustomerTypes]    = useState([]);
    const [partnerOptions,      setPartnerOptions]      = useState([]);
    // Organisation-wide switch (Settings → Projects). When on, the form gets a
    // Project picker; the options are the projects this user can switch to.
    const [projectAssignOn,     setProjectAssignOn]     = useState(false);
    const [projectOptions,      setProjectOptions]      = useState([]);
    const activeProjectId = Number(sessionStorage.getItem('project_id')) || null;

    // ── modal state ──────────────────────────────────────────────────────
    const [openDialog,             setOpenDialog]             = useState(false);
    const [isDeleteConfirmOpen,    setIsDeleteConfirmOpen]    = useState(false);
    const [showAssigneeLog,        setShowAssigneeLog]        = useState(false);
    const [showAssigneeLogForUser, setShowAssigneeLogForUser] = useState(false);
    const [selectedRow,            setSelectedRow]            = useState(null);
    const [selectedRows,           setSelectedRows]           = useState([]);
    const [editMode,               setEditMode]               = useState(false);
    const [saving,                 setSaving]                 = useState(false);
    // Add Lead form: who already has this number (customer/dedupe/check/), and
    // whether the rep chose to create a new lead anyway.
    const [dupCheck,               setDupCheck]               = useState(null);
    const [dupChoice,              setDupChoice]              = useState(null);
    const [dupReason,              setDupReason]              = useState('');

    // ── form state ───────────────────────────────────────────────────────
    const [formData, setFormData] = useState({
        id: null, customer_name: "", customer_type: "", notes: "",
        lead_stage: defaultCategory === 'channel_partner' ? "Onboarding" : "New Lead",
        lead_source: "", sourced_by_partner: null, assigned_to: "", address: "", country: DEFAULT_COUNTRY, state: "", city: "",
        contact_name: "", contact_number: "", is_active: true,
        preferred_language: "en", project_id: null, extra_project_ids: [],
    });

    const preferredLanguageOptions = [
        { value: 'en', label: 'English' },
        { value: 'hi', label: 'Hindi' },
        { value: 'ta', label: 'Tamil' },
        { value: 'ml', label: 'Malayalam' },
        { value: 'te', label: 'Telugu' },
    ];

    const leadSourceOptions = [
        { value: 'Meta', label: 'Meta' },
        { value: 'Website', label: 'Website' },
        { value: 'Instagram', label: 'Instagram' },
        { value: 'Linked In', label: 'Linked In' },
        { value: 'Whats app', label: 'Whats app' },
        { value: 'Referal', label: 'Referal' },
        { value: 'Lusha', label: 'Lusha' },
        { value: 'Direct Visit', label: 'Direct Visit' },
        { value: CHANNEL_PARTNER_SOURCE, label: CHANNEL_PARTNER_SOURCE },
    ];

    // ── load ─────────────────────────────────────────────────────────────
    const load = (overrides = {}) => {
        setLoading(true);
        const pg   = overrides.pg   !== undefined ? overrides.pg   : page;
        const ps   = overrides.ps   !== undefined ? overrides.ps   : pageSize;
        const sf   = overrides.sf   !== undefined ? overrides.sf   : stageFilter;
        const nr   = overrides.nr   !== undefined ? overrides.nr   : needsReassignmentFilter;
        // Quick views are one at a time; a pair persisted by the old chip row
        // resolves to Needs reassignment.
        const dfRaw = overrides.df  !== undefined ? overrides.df   : dupFilter;
        const df   = nr ? '' : dfRaw;
        const uid  = overrides.uid  !== undefined ? overrides.uid  : selectedUserId;
        const ctyp = overrides.ctyp !== undefined ? overrides.ctyp : selectedCustomerType;
        const cn   = overrides.cn   !== undefined ? overrides.cn   : customerName;
        const q    = overrides.q    !== undefined ? overrides.q    : search;
        const dp   = overrides.dp   !== undefined ? overrides.dp   : datePreset;
        const cf   = overrides.cf   !== undefined ? overrides.cf   : customFrom;
        const cto  = overrides.cto  !== undefined ? overrides.cto  : customTo;
        const cc   = overrides.cc   !== undefined ? overrides.cc   : customerCategory;
        const cp   = overrides.cp   !== undefined ? overrides.cp   : partnerFilter;
        const st   = overrides.st   !== undefined ? overrides.st   : stateFilter;

        const { from_date, to_date } = resolveDateRange(dp, cf, cto);
        const params = {
            page_no: pg, page_size: ps,
            ...(from_date && { from_date }),
            ...(to_date   && { to_date }),
            ...(uid       && { assigned_to_id: uid }),
            ...(sf        && { lead_stage: sf }),
            ...(nr        && { needs_reassignment: true }),
            ...(ctyp         && { customer_type: ctyp }),
            ...(cn.trim()    && { customer_name: cn.trim() }),
            ...(q.trim()     && { search: q.trim() }),
            ...(cc           && { customer_category: cc }),
            ...(cp           && { sourced_by_partner: cp }),
            ...(st           && { state: st }),
            ...(df           && { dup_filter: df }),
        };
        fetch(CUSTOMERSLIST, params)
            .then(res => {
                if (res?.status !== 'fail') {
                    setData(Array.isArray(res.data) ? res.data : []);
                    setTotal(res.total_count || 0);
                    // Always the full set of stages, and the full total beside
                    // them: the API computes both without the lead_stage filter
                    // applied, so the badge row stays whole while one stage is
                    // selected. Falls back to total_count against an API that
                    // predates all_count.
                    setAllCount(res.all_count ?? res.total_count ?? 0);
                    if (res.status_count) setStatusCount(res.status_count);
                    if (Array.isArray(res.state_counts)) setStateCounts(res.state_counts);
                    if (res.dup_counts) setDupCounts(res.dup_counts);
                    setNeedsReassignCount(res.needs_reassignment_count ?? null);
                } else {
                    ToastMsg('error', res.message || 'Failed to load customers');
                }
            })
            .catch(() => ToastMsg('error', 'Failed to load customers'))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        load();
        fetch(USERS_SIMPLE_URL).then(res => { if (res?.status === 'success') setRepUsers(res.data || []); });
        fetch(USERSLIST_URL).then(res => {
            if (res?.status !== 'fail') {
                setUserList((res.data || []).map(u => ({
                    id: u.id, value: u.emp_code,
                    label: `${u.login_fname} ${u.login_lname}`,
                    emp_code: u.emp_code, role_type: u.role_type, email: u.login_email_address,
                })));
            }
        });
        fetch(CUSTOMER_TYPE_MASTER_URL).then(res => {
            if (res?.status === 'success') {
                const active = (res.data || []).filter(t => t.is_active);
                setAllCustomerTypes(active);
                setCustomerTypeOptions(active.map(t => ({ value: t.name, label: t.name })));
            }
        });
        // Loaded once rather than on each dropdown open: the picker is inside a
        // modal, and fetching there put a spinner in front of a rep mid-form.
        fetch(CHANNEL_PARTNER_OPTIONS_URL, { limit: 200 }).then(res => {
            if (res?.status === 'success') setPartnerOptions(res.data || []);
        }).catch(() => {});
        fetch(PROJECT_ASSIGNMENT_CONFIG_URL).then(res => {
            if (res?.status !== 'success' || !res.is_enabled) return;
            setProjectAssignOn(true);
            fetch(PROJECTS_URL).then(r => {
                if (r?.status === 'success') setProjectOptions((r.data || []).filter(p => p.is_active));
            });
        }).catch(() => {});
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ── customer name autocomplete ────────────────────────────────────────
    useEffect(() => {
        if (!cnInput.trim()) { setCnSuggestions([]); setCnOpen(false); return; }
        const t = setTimeout(() => {
            fetch(CUSTOMERSLIST, { customer_name: cnInput.trim(), page_no: 1, page_size: 10 })
                .then(res => {
                    if (res?.status !== 'fail') {
                        const names = [...new Set((res.data || []).map(r => r.customer_name).filter(Boolean))];
                        setCnSuggestions(names);
                        setCnOpen(names.length > 0);
                    }
                }).catch(() => {});
        }, 300);
        return () => clearTimeout(t);
    }, [cnInput]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const handleClick = (e) => { if (cnRef.current && !cnRef.current.contains(e.target)) setCnOpen(false); };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    // ── duplicate check while typing the number (new prospects only) ─────
    useEffect(() => {
        if (!openDialog || editMode || defaultCategory === 'channel_partner') { setDupCheck(null); return; }
        const digits = String(formData.contact_number || '').replace(/\D/g, '');
        if (digits.length < 10) { setDupCheck(null); setDupChoice(null); return; }
        const t = setTimeout(() => {
            fetch(DEDUPE_CHECK_URL, { phone: digits })
                .then(res => {
                    if (res?.status === 'success' && res.matches?.length) setDupCheck(res);
                    else { setDupCheck(null); setDupChoice(null); }
                }).catch(() => {});
        }, 400);
        return () => clearTimeout(t);
    }, [formData.contact_number, openDialog, editMode]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── stage badges ──────────────────────────────────────────────────────
    // status_count arrives unaffected by the active stage filter, so the badge
    // row stays complete while one stage is selected — and survives leaving the
    // page and coming back, which the old in-memory copy did not.
    const stages = useMemo(() => {
        const present = new Set(Object.keys(statusCount).filter(Boolean));
        const ordered = STAGE_ORDER.filter(s => present.has(s));
        present.forEach(s => { if (!ordered.includes(s)) ordered.push(s); });
        return ordered;
    }, [statusCount]);

    const totalPages = Math.max(1, Math.ceil(total / pageSize));

    // ── handlers ─────────────────────────────────────────────────────────
    const handleApply = () => { setPage(1); load({ pg: 1 }); };

    const handleStageClick = (stage) => {
        const newStage = stageFilter === stage ? "" : stage;
        setStageFilter(newStage);
        setPage(1);
        load({ pg: 1, sf: newStage });
    };

    // ── quick views ──────────────────────────────────────────────────────
    const activeView = needsReassignmentFilter ? 'needs_reassignment' : (dupFilter || '');
    const viewCount = (key) => (key === 'needs_reassignment' ? needsReassignCount : dupCounts[key]);
    // An empty queue is hidden, unless it is the open one (so it can still be closed).
    const visibleViews = QUICK_VIEWS.filter(v =>
        (defaultCategory !== 'channel_partner' || v.partnersToo)
        && (activeView === v.key || viewCount(v.key) !== 0));
    const activeViewMeta = QUICK_VIEWS.find(v => v.key === activeView);

    const handleViewChange = (key) => {
        const nr = key === 'needs_reassignment';
        const df = nr ? '' : key;
        setNeedsReassignmentFilter(nr);
        setDupFilter(df);
        setSelectedRows([]);
        setPage(1);
        load({ pg: 1, nr, df });
    };

    // Everything below the view tabs; the view itself has its own "All leads" tab.
    const activeFilterCount = [
        datePreset !== 'all', customerName, selectedCustomerType, stateFilter,
        partnerFilter, selectedUserId, stageFilter, search.trim(),
    ].filter(Boolean).length;

    const resetFilters = () => {
        setDatePreset('all'); setCustomFrom(''); setCustomTo('');
        setCnInput(''); setCustomerName('');
        setSelectedCustomerType(''); setStateFilter(''); setPartnerFilter('');
        setSelectedUserId(''); setStageFilter(''); setSearch('');
        setPage(1);
        load({ pg: 1, dp: 'all', cf: '', cto: '', cn: '', ctyp: '', st: '', cp: '', uid: '', sf: '', q: '' });
    };

    // Needs-reassignment queue: pick up everything on this page and open Assign.
    const assignAllOnPage = () => {
        if (!data.length) return;
        setSelectedRows(data.map(r => r.id));
        setShowAssigneeLogForUser(true);
    };

    const handlePageChange = (newPage) => { setPage(newPage); load({ pg: newPage }); };

    const handlePageSizeChange = (ps) => { setPageSize(ps); setPage(1); load({ pg: 1, ps }); };

    const toggleRow = (id) => setSelectedRows(prev => prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]);
    const toggleAll = () => setSelectedRows(prev => prev.length === data.length ? [] : data.map(r => r.id));

    const resetForm = () => {
        setFormData({ id: null, customer_name: "", customer_type: "", notes: "",
            lead_stage: defaultCategory === 'channel_partner' ? "Onboarding" : "New Lead",
            lead_source: "", sourced_by_partner: null, assigned_to: "", address: "", country: DEFAULT_COUNTRY, state: "", city: "",
            contact_name: "", contact_number: "", is_active: true,
            preferred_language: "en", project_id: null, extra_project_ids: [] });
        setEditMode(false); setOpenDialog(false); setSelectedRow(null);
        setDupCheck(null); setDupChoice(null); setDupReason('');
    };

    const handleDraft = async () => {
        try {
            setSaving(true);
            const res = await create(CUSTOMERCREATE, { ...formData, is_draft: true, lead_stage: formData.lead_stage,
                status: { lead_stage: formData.lead_stage, is_active: formData.is_active } });
            if (res?.status === "success") { ToastMsg("success", res.message || "Saved to draft"); resetForm(); load(); }
            else ToastMsg("error", res?.message || "Failed to save draft");
        } catch { ToastMsg("error", "Failed to save draft"); } finally { setSaving(false); }
    };

    const handleSave = async () => {
        // Enforced here as well as being starred on the label: a lead saved as
        // "came from a channel partner" with no partner named is exactly the
        // unattributed row this feature exists to stop. Drafts stay exempt —
        // they are incomplete by definition.
        if (isPartnerSource(formData.lead_source) && !formData.sourced_by_partner) {
            ToastMsg("error", "Select the channel partner this lead came from");
            return;
        }
        try {
            setSaving(true);
            const res = await create(CUSTOMERCREATE, { ...formData, customer_detail_id: formData.id, is_draft: false,
                customer_description: formData.notes, status: { lead_stage: formData.lead_stage, is_active: formData.is_active },
                ...(!editMode && dupChoice === 'create' && { duplicate_override: true, duplicate_reason: dupReason.trim() }) });
            if (res?.status === "fail" && Array.isArray(res.matches)) {
                // 409: the number is already a lead and the rules are on. The
                // card under Contact Number says what to do about it.
                const primary = res.matches.find(m => m.id === res.primary_id) || res.matches[0];
                setDupCheck({ matches: res.matches, primary_id: res.primary_id, is_enabled: true,
                              require_reason: res.require_reason,
                              hint: primary?.state === 'closed' ? 'reopen' : primary?.state === 'customer' ? 'customer' : 'attach' });
                ToastMsg("error", "This number is already a lead - choose what to do under Contact Number");
                return;
            }
            if (res?.status === "success") {
                ToastMsg("success", res.message || `Customer ${editMode ? 'updated' : 'saved'}`);
                // A lead filed under another project drops out of this list,
                // which reads as data loss unless it is said out loud.
                if (projectAssignOn && formData.project_id && activeProjectId && formData.project_id !== activeProjectId
                        && !(formData.extra_project_ids || []).includes(activeProjectId)) {
                    const name = projectOptions.find(p => p.id === formData.project_id)?.name || 'another project';
                    ToastMsg("info", `Filed under ${name} — switch project to see it`);
                }
                resetForm(); load();
            }
            else ToastMsg("error", res?.message || `Failed to ${editMode ? 'update' : 'save'} customer`);
        } catch { ToastMsg("error", `Failed to ${editMode ? 'update' : 'save'} customer`); } finally { setSaving(false); }
    };

    // "Add as enquiry to it": no new lead; the existing one is told they came
    // back (and reopened if it had been closed).
    const handleAttachEnquiry = async (match) => {
        try {
            setSaving(true);
            const res = await create(DEDUPE_ATTACH_URL, {
                customer_id: match.id, source: formData.lead_source || 'Added by hand',
                name: formData.contact_name || formData.customer_name,
                phone: formData.contact_number, message: formData.notes,
            });
            if (res?.status === "success") { ToastMsg("success", res.message); resetForm(); load(); }
            else ToastMsg("error", res?.message || "Could not add the enquiry");
        } catch { ToastMsg("error", "Could not add the enquiry"); } finally { setSaving(false); }
    };

    const openExistingLead = (match) => {
        resetForm();
        navigate(`/customers/customerdetails?id=${match.id}`, { state: { formEdit: true, row: { id: match.id } } });
    };

    const handleDraftDelete = async () => {
        try {
            setSaving(true);
            const res = await remove(CUSTOMERCREATE, { ...selectedRow, draft_delete: true }, setSaving);
            if (res?.status === "success") { ToastMsg("success", "Draft deleted"); load(); }
            else ToastMsg("error", res?.message || "Failed to delete");
        } catch { ToastMsg("error", "Failed to delete"); }
        finally { setSaving(false); setSelectedRow(null); setIsDeleteConfirmOpen(false); }
    };

    const handleDelete = async () => {
        try {
            setSaving(true);
            const res = await remove(CUSTOMERCREATE, { ...selectedRow, is_draft: false, is_delete: true }, setSaving);
            if (res?.status === "success") { ToastMsg("success", "Customer deleted"); load(); }
            else ToastMsg("error", res?.message || "Failed to delete");
        } catch { ToastMsg("error", "Failed to delete"); }
        finally { setSaving(false); setSelectedRow(null); setIsDeleteConfirmOpen(false); }
    };

    // Bulk assign through customer/assignment/assign/: to one person, or shared
    // across a team with its strategy and limits. Partial success is normal.
    const [assignForm, setAssignForm] = useState({ mode: 'user', user_id: null, team_id: null, reason: '' });
    const [assignMeta, setAssignMeta] = useState({ users: [], teams: [], requireReason: false });
    useEffect(() => {
        if (!showAssigneeLogForUser) return;
        fetch(ASSIGNMENT_SETTINGS_URL).then(res => {
            if (res?.status === 'success') setAssignMeta({
                users: res.users || [], teams: (res.teams || []).filter(t => t.is_active),
                requireReason: !!res.settings?.require_reassign_reason });
        });
    }, [showAssigneeLogForUser]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleAssignedToUser = async () => {
        if (assignForm.mode === 'user' && !assignForm.user_id) { ToastMsg("error", "Pick a person"); return; }
        if (assignForm.mode === 'team' && !assignForm.team_id) { ToastMsg("error", "Pick a team"); return; }
        try {
            setSaving(true);
            const res = await create(ASSIGNMENT_ASSIGN_URL, { lead_ids: selectedRows, ...assignForm });
            if (res?.status === "success") {
                ToastMsg(res.skipped ? "warning" : "success", res.message); setShowAssigneeLogForUser(false);
                setAssignForm({ mode: 'user', user_id: null, team_id: null, reason: '' }); setSelectedRows([]); load();
            } else ToastMsg("error", res?.message || "Failed to assign");
        } catch { ToastMsg("error", "Failed to assign"); } finally { setSaving(false); }
    };

    const handlePushToAICaller = async () => {
        try {
            setSaving(true);
            const res = await create(AI_CALLER_BUCKET_URL, { customer_ids: selectedRows });
            if (res?.status === "success") {
                const { added, skipped_existing, skipped_no_phone } = res.data || {};
                let msg = `${added} lead(s) pushed to AI Caller`;
                if (skipped_existing) msg += `, ${skipped_existing} already in bucket`;
                if (skipped_no_phone) msg += `, ${skipped_no_phone} skipped (no phone)`;
                ToastMsg("success", msg);
                setSelectedRows([]);
            } else ToastMsg("error", res?.message || "Failed to push leads");
        } catch { ToastMsg("error", "Failed to push leads to AI Caller"); } finally { setSaving(false); }
    };

    const openEditDialog = (row) => {
        setEditMode(true);
        setFormData({
            id: row?.id || null,
            customer_name: row?.customer_name || "",
            customer_type: row?.customer_type || "",
            notes: row?.customer_description || "",
            lead_stage: row?.lead_stage?.length > 0 ? row?.lead_stage : "New Lead",
            lead_source: row?.lead_source || "",
            sourced_by_partner: row?.sourced_by_partner || null,
            assigned_to: row?.assigned_to_id || "",
            address: row?.address || "",
            country: row?.country || DEFAULT_COUNTRY,
            // Older leads saved the state's ISO code ("TN"); show and re-save the name.
            state: stateName(row?.country || DEFAULT_COUNTRY, row?.state),
            city: row?.city || "",
            contact_name: row?.contact_name || "",
            contact_number: row?.contact_number || "",
            is_active: row?.is_active ?? true,
            preferred_language: row?.preferred_language || "en",
            project_id: row?.project_id || null,
            extra_project_ids: (row?.extra_projects || []).map(p => p.id),
            // Names for links into projects this user is not a member of, so
            // the picker shows a name rather than a bare id. The API ignores it.
            extra_projects_known: row?.extra_projects || [],
        });
        setOpenDialog(true);
    };

    // A partner's own row has no partner behind it, so the column and its filter
    // are prospect-side only. The partner page gets the mirror image instead:
    // how many leads that partner has sent us.
    const showPartnerColumn = defaultCategory !== 'channel_partner';
    const showSourcedCount  = defaultCategory === 'channel_partner';
    const colSpan = (sessionStorageRole !== 'third_party' ? 10 : 9) + 1;

    const PROSPECT_STAGES = ['New Lead','Contacted','Qualified','Proposal','Negotiation','Closed Won','Closed Lost','On Hold','Unanswered','Demo Fixed','Demo Done','No Show','Dis-Qualified','Nurture Lead'];
    const CP_STAGES = ['Onboarding','Agreement Signed','Active','Inactive','Suspended','Terminated'];

    const isFormTypeCP = allCustomerTypes.find(t => t.name === formData.customer_type)?.is_channel_partner || false;
    const formStageOptions = isFormTypeCP ? CP_STAGES : PROSPECT_STAGES;

    return (
        <ContainerCard>
            <div className="flex-shrink-0">

                {/* ── header ── */}
                <div className="flex items-center justify-between mb-4 px-1">
                    <div>
                        <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                            {defaultCategory === 'channel_partner' ? 'Channel Partners' : 'Customer Directory'}
                        </h1>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            {defaultCategory === 'channel_partner'
                                ? 'Manage your channel partner network'
                                : 'Track and manage your customer database'}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        {selectedRows.length > 0 && (
                            <>
                                <Button onClick={handlePushToAICaller} size="sm" variant="outline" className="text-xs flex items-center gap-1" disabled={saving}>
                                    <Phone className="h-3 w-3" />
                                    AI Caller ({selectedRows.length})
                                </Button>
                                <Button onClick={() => setShowAssigneeLogForUser(true)} size="sm" variant="outline" className="text-xs flex items-center gap-1">
                                    <Users className="h-3 w-3" />
                                    Assign ({selectedRows.length})
                                </Button>
                            </>
                        )}
                        <Button onClick={() => load()} size="sm" variant="outline" className="flex items-center gap-1 text-xs" disabled={loading}>
                            <RefreshCw className={cn("h-3 w-3", loading && "animate-spin")} />
                            Refresh
                        </Button>
                        <button
                            onClick={() => setOpenDialog(true)}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-primary hover:bg-primary/90 text-white rounded-lg transition-colors"
                        >
                            <Plus className="h-3.5 w-3.5" />
                            {defaultCategory === 'channel_partner' ? 'Add Channel Partner' : 'Add Customer'}
                        </button>
                    </div>
                </div>

                {/* ── quick views: work queues, one at a time ── */}
                <div className="flex items-end gap-1 border-b border-gray-200 dark:border-gray-700 mb-3 overflow-x-auto">
                    <button
                        onClick={() => handleViewChange('')}
                        className={cn("flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 -mb-px whitespace-nowrap transition-colors",
                            !activeView
                                ? "border-primary text-primary"
                                : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200")}
                    >
                        <Inbox className="h-3.5 w-3.5" />
                        All leads
                    </button>
                    {visibleViews.map(v => {
                        const Icon = v.icon;
                        const n = viewCount(v.key);
                        const on = activeView === v.key;
                        return (
                            <button key={v.key} title={v.hint}
                                onClick={() => handleViewChange(on ? '' : v.key)}
                                className={cn("flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 -mb-px whitespace-nowrap transition-colors",
                                    on
                                        ? "border-primary text-gray-900 dark:text-white"
                                        : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200")}
                            >
                                <Icon className={cn("h-3.5 w-3.5", v.tone)} />
                                {v.label}
                                {n !== undefined && n !== null && (
                                    <span className={cn("min-w-[1.25rem] px-1.5 py-px rounded-full text-[10px] font-semibold text-center",
                                        on ? "bg-primary text-white"
                                           : n > 0 ? "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200"
                                                   : "bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500")}>
                                        {n}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>

                {/* What the open view means, and what to do with it. */}
                {activeViewMeta && (
                    <div className="flex flex-wrap items-center gap-2 mb-3 px-3 py-2 rounded-lg bg-primary/5 border border-primary/20 text-xs">
                        <activeViewMeta.icon className={cn("h-3.5 w-3.5 flex-shrink-0", activeViewMeta.tone)} />
                        <span className="text-gray-700 dark:text-gray-300">
                            <b className="font-semibold">{activeViewMeta.label}:</b> {activeViewMeta.hint}
                        </span>
                        <div className="ml-auto flex items-center gap-2">
                            {activeView === 'needs_reassignment' && data.length > 0 && (
                                <Button onClick={assignAllOnPage} size="sm" className="text-xs h-7 flex items-center gap-1 bg-primary hover:bg-primary/90 text-white">
                                    <Users className="h-3 w-3" />
                                    Reassign all {data.length} on this page
                                </Button>
                            )}
                            <button onClick={() => handleViewChange('')}
                                className="flex items-center gap-1 text-gray-500 hover:text-gray-800 dark:hover:text-gray-200">
                                <X className="h-3 w-3" /> Close view
                            </button>
                        </div>
                    </div>
                )}

                {/* ── filters ── */}
                <div className="flex flex-wrap items-center gap-2 mb-3">
                    <div className="relative">
                        <select
                            value={datePreset}
                            onChange={e => { const v = e.target.value; setDatePreset(v); if (v !== "custom") { setCustomFrom(""); setCustomTo(""); load({ pg: 1, dp: v, cf: "", cto: "" }); } }}
                            className={cn(selectCls, datePreset !== 'all' && selectActiveCls)}
                        >
                            {DATE_PRESETS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                        </select>
                        <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                    </div>
                    {datePreset === "custom" && (
                        <>
                            <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
                                className="text-xs border border-gray-200 dark:border-gray-700 rounded-lg px-2.5 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none focus:ring-1 focus:ring-primary" />
                            <span className="text-xs text-gray-400">to</span>
                            <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
                                className="text-xs border border-gray-200 dark:border-gray-700 rounded-lg px-2.5 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none focus:ring-1 focus:ring-primary" />
                            <Button onClick={handleApply} size="sm" variant="outline" className="text-xs px-3" disabled={loading}>Apply</Button>
                        </>
                    )}
                    <div ref={cnRef} className="relative">
                        <input
                            type="text"
                            value={cnInput}
                            onChange={e => { setCnInput(e.target.value); if (!e.target.value) setCustomerName(""); }}
                            onKeyDown={e => { if (e.key === 'Enter') { setCustomerName(cnInput); setCnOpen(false); load({ pg: 1, cn: cnInput }); } if (e.key === 'Escape') setCnOpen(false); }}
                            onFocus={() => cnSuggestions.length > 0 && setCnOpen(true)}
                            placeholder="Customer name…"
                            className={cn("pl-3 pr-6 py-1.5 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none focus:ring-1 focus:ring-primary w-44",
                                customerName && selectActiveCls)}
                        />
                        {cnInput && (
                            <button onClick={() => { setCnInput(""); setCustomerName(""); setCnOpen(false); load({ pg: 1, cn: "" }); }}
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                                <span className="text-xs leading-none">×</span>
                            </button>
                        )}
                        {cnOpen && cnSuggestions.length > 0 && (
                            <div className="absolute z-50 top-full mt-1 left-0 w-56 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg overflow-hidden">
                                {cnSuggestions.map((name, i) => (
                                    <button key={i} type="button"
                                        onMouseDown={() => { setCnInput(name); setCustomerName(name); setCnOpen(false); load({ pg: 1, cn: name }); }}
                                        className="w-full text-left px-3 py-2 text-xs text-gray-700 dark:text-gray-300 hover:bg-primary/5 hover:text-primary truncate"
                                    >
                                        {name}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    {defaultCategory !== 'channel_partner' && (
                    <div className="relative">
                        <select value={selectedCustomerType} onChange={e => { const v = e.target.value; setSelectedCustomerType(v); load({ pg: 1, ctyp: v }); }}
                            className={cn(selectCls, selectedCustomerType && selectActiveCls)}
                        >
                            <option value="">All Types</option>
                            {customerTypeOptions.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                        <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                    </div>
                    )}
                    {defaultCategory !== 'channel_partner' && (
                    <div className="relative">
                        <select value={stateFilter} onChange={e => { const v = e.target.value; setStateFilter(v); setPage(1); load({ pg: 1, st: v }); }}
                            className={cn(selectCls, stateFilter && selectActiveCls)}
                            title="Show only leads from one state"
                        >
                            <option value="">All States</option>
                            {stateCounts.filter(s => s.state !== '__blank__').map(s => (
                                <option key={s.state} value={s.state}>{s.state} ({s.count})</option>
                            ))}
                            {stateCounts.some(s => s.state === '__blank__') && (
                                <option value="__blank__">
                                    No state ({stateCounts.find(s => s.state === '__blank__').count})
                                </option>
                            )}
                            {/* Keep a persisted pick visible even when no lead matches it now. */}
                            {stateFilter && !stateCounts.some(s => s.state.toLowerCase() === stateFilter.toLowerCase()) && (
                                <option value={stateFilter}>{stateFilter === '__blank__' ? 'No state' : stateFilter} (0)</option>
                            )}
                        </select>
                        <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                    </div>
                    )}
                    {showPartnerColumn && (
                    <div className="relative">
                        <select value={partnerFilter} onChange={e => { const v = e.target.value; setPartnerFilter(v); setPage(1); load({ pg: 1, cp: v }); }}
                            className={cn(selectCls, partnerFilter && selectActiveCls)}
                            title="Show only leads a given channel partner sent"
                        >
                            <option value="">All Partners</option>
                            {partnerOptions.map(p => (
                                <option key={p.id} value={p.id}>{p.customer_name}{p.sourced_count ? ` (${p.sourced_count})` : ''}</option>
                            ))}
                        </select>
                        <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                    </div>
                    )}
                    <div className="relative">
                        <select value={selectedUserId} onChange={e => { const v = e.target.value; setSelectedUserId(v); load({ pg: 1, uid: v }); }}
                            className={cn(selectCls, selectedUserId && selectActiveCls)}
                        >
                            <option value="">All Reps</option>
                            {repUsers.map(u => {
                                const name = [u.login_fname, u.login_lname].filter(Boolean).join(" ") || u.login_name;
                                return <option key={u.id} value={u.id}>{name}</option>;
                            })}
                        </select>
                        <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                    </div>
                    {datePreset !== "all" && (
                        <span className="text-[10px] text-gray-400 dark:text-gray-500">
                            {getDisplayLabel(datePreset, customFrom, customTo)}
                        </span>
                    )}
                    {activeFilterCount > 0 && (
                        <button onClick={resetFilters}
                            className="flex items-center gap-1 px-2 py-1.5 text-xs text-gray-500 hover:text-red-600 dark:hover:text-red-400"
                            title="Clear every filter below the view tabs">
                            <FilterX className="h-3.5 w-3.5" />
                            Reset {activeFilterCount} filter{activeFilterCount === 1 ? '' : 's'}
                        </button>
                    )}
                    <div className="ml-auto flex items-center gap-1.5 border border-gray-200 dark:border-gray-700 rounded-lg px-2.5 py-1.5 bg-white dark:bg-gray-800">
                        <Search className="h-3 w-3 text-gray-400" />
                        <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && load({ pg: 1, q: search })}
                            placeholder="Search name, phone… (Enter)"
                            className="text-xs bg-transparent outline-none text-gray-700 dark:text-gray-300 w-44" />
                    </div>
                </div>

                {/* ── stage pills: counts are within the open view and filters ── */}
                <div className="flex flex-wrap items-center gap-2 mb-4">
                    <span className="text-[10px] uppercase tracking-wide font-semibold text-gray-400 dark:text-gray-500 mr-1">Stage</span>
                    <button
                        onClick={() => handleStageClick("")}
                        className={cn(
                            "px-3 py-1 rounded-full text-xs font-medium border transition-colors",
                            !stageFilter
                                ? "bg-gray-700 text-white border-gray-800"
                                : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-200 dark:hover:bg-gray-700"
                        )}
                        title={activeViewMeta ? `Every open stage within ${activeViewMeta.label}` : 'Every open stage'}
                    >
                        All stages · {allCount}
                    </button>
                    {stages.map(stage => {
                        const count = statusCount[stage] || 0;
                        return (
                            <button key={stage}
                                onClick={() => handleStageClick(stage)}
                                className={cn("px-3 py-1 rounded-full text-xs font-medium border transition-colors", getStageFilterClass(stage, stageFilter === stage))}
                            >
                                {stage} · {count}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* ── table ── */}
            {loading ? (
                <div className="flex items-center justify-center flex-1">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sidebar-primary mx-auto" />
                        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">Loading customers…</p>
                    </div>
                </div>
            ) : (
                <div className="flex-1 min-h-0 flex flex-col rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="flex-1 min-h-0 overflow-auto">
                        <table className="w-full text-xs">
                            <thead className="sticky top-0 z-10">
                                <tr className="bg-gray-50 dark:bg-gray-700/50 text-left">
                                    <th className="px-3 py-2.5 w-8">
                                        <input type="checkbox"
                                            checked={data.length > 0 && selectedRows.length === data.length}
                                            onChange={toggleAll}
                                            className="rounded border-gray-300" />
                                    </th>
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300 w-8">#</th>
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Created</th>
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Customer</th>
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Type</th>
                                    {showPartnerColumn && (
                                        <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Sourced By</th>
                                    )}
                                    {showSourcedCount && (
                                        <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Leads Sent</th>
                                    )}
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Assigned To</th>
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Stage</th>
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Recent Update</th>
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Next Follow-up</th>
                                    {sessionStorageRole !== 'third_party' && (
                                        <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300 text-right">Actions</th>
                                    )}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                {data.length === 0 ? (
                                    <tr>
                                        <td colSpan={colSpan} className="text-center py-10 text-xs text-gray-400 dark:text-gray-500">
                                            No customers found
                                        </td>
                                    </tr>
                                ) : data.map((row, idx) => (
                                    <tr
                                        key={row.id || idx}
                                        className={cn("hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer transition-colors",
                                            // Verified by the lead (email / WhatsApp link) outranks a priority source.
                                            row.is_verified
                                                ? "bg-emerald-50/70 dark:bg-emerald-900/15 shadow-[inset_3px_0_0_0_theme(colors.emerald.500)]"
                                                : row.is_priority && "bg-orange-50/50 dark:bg-orange-900/10 shadow-[inset_3px_0_0_0_theme(colors.orange.400)]")}
                                        onClick={(e) => {
                                            if (!row?.current_assignee) return;
                                            if (!e.target.closest('button') && !e.target.closest('input') && !e.target.closest('a')) {
                                                navigate('/customers/customerdetails', { state: { formEdit: true, row } });
                                            }
                                        }}
                                    >
                                        <td className="px-3 py-2.5" onClick={e => e.stopPropagation()}>
                                            <input type="checkbox"
                                                checked={selectedRows.includes(row.id)}
                                                onChange={() => toggleRow(row.id)}
                                                className="rounded border-gray-300" />
                                        </td>
                                        <td className="px-3 py-2.5 text-gray-400 dark:text-gray-500">
                                            {(page - 1) * pageSize + idx + 1}
                                        </td>
                                        <td className="px-3 py-2.5 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                                            {row.created_date ? (
                                                <>
                                                    <div>{formatDate(row.created_date)}</div>
                                                    <div className="text-[10px] text-gray-400 dark:text-gray-500">
                                                        {formatTime(row.created_date)}
                                                    </div>
                                                </>
                                            ) : "—"}
                                        </td>
                                        <td className="px-3 py-2.5 max-w-[170px]">
                                            <p className="flex items-center gap-1 min-w-0">
                                                <span className="font-medium text-gray-800 dark:text-gray-200 truncate">{row.customer_name || "—"}</span>
                                                <ResearchMark research={row.research} />
                                                {row.is_verified && (
                                                    <span title="Email or mobile verified by the lead through an email / WhatsApp link"
                                                        className="inline-flex flex-shrink-0 items-center gap-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/40 px-1.5 py-px text-[9px] font-semibold text-emerald-700 dark:text-emerald-300">
                                                        <CheckCircle className="h-2.5 w-2.5" aria-hidden="true" />Verified
                                                    </span>
                                                )}
                                            </p>
                                            {projectAssignOn && activeProjectId && row.project_id && row.project_id !== activeProjectId && (
                                                <p className="text-[10px] text-blue-600 dark:text-blue-400 truncate" title="Shared into this project; its main project is elsewhere">
                                                    Shared from {row.project_name || 'another project'}
                                                </p>
                                            )}
                                            {projectAssignOn && row.extra_projects?.length > 0 && row.project_id === activeProjectId && (
                                                <p className="text-[10px] text-blue-600 dark:text-blue-400 truncate"
                                                   title={row.extra_projects.map(p => p.name).join(', ')}>
                                                    Also in {row.extra_projects.length} project{row.extra_projects.length === 1 ? '' : 's'}
                                                </p>
                                            )}
                                            {row.city && (
                                                <p className="flex items-center gap-0.5 text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">
                                                    <MapPin className="h-2.5 w-2.5" />{row.city}
                                                </p>
                                            )}
                                            <LeadQualityBadges row={row} />
                                            <DuplicateBadges row={row} />
                                        </td>
                                        <td className="px-3 py-2.5 text-gray-500 dark:text-gray-400">
                                            <div className="flex flex-col gap-0.5">
                                                <span>{row.customer_type || "—"}</span>
                                                {row.customer_category === 'channel_partner' && (
                                                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 w-fit">CP</span>
                                                )}
                                            </div>
                                        </td>
                                        {showPartnerColumn && (
                                            <td className="px-3 py-2.5 max-w-[150px]">
                                                {row.sourced_by_partner_name ? (
                                                    <span
                                                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-50 text-purple-700 border border-purple-200 dark:bg-purple-900/20 dark:text-purple-300 dark:border-purple-800/50 max-w-full"
                                                        title={`Introduced by ${row.sourced_by_partner_name}`}
                                                    >
                                                        <Handshake className="h-2.5 w-2.5 flex-shrink-0" />
                                                        <span className="truncate">{row.sourced_by_partner_name}</span>
                                                    </span>
                                                ) : (
                                                    <span className="text-gray-400 dark:text-gray-500 text-[10px]">
                                                        {row.lead_source || "—"}
                                                    </span>
                                                )}
                                            </td>
                                        )}
                                        {showSourcedCount && (
                                            <td className="px-3 py-2.5">
                                                {row.sourced_leads_count ? (
                                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-purple-50 text-purple-700 border border-purple-200 dark:bg-purple-900/20 dark:text-purple-300 dark:border-purple-800/50">
                                                        <Handshake className="h-2.5 w-2.5" />
                                                        {row.sourced_leads_count}
                                                    </span>
                                                ) : (
                                                    <span className="text-gray-300 dark:text-gray-600 text-[10px]">none yet</span>
                                                )}
                                            </td>
                                        )}
                                        <td className="px-3 py-2.5 text-gray-600 dark:text-gray-400 max-w-[120px] truncate">
                                            {row.current_assignee || "—"}
                                        </td>
                                        <td className="px-3 py-2.5">
                                            <div className="flex items-center gap-1 flex-wrap">
                                                {row.lead_stage ? (
                                                    <span className={cn("px-2 py-0.5 rounded-full text-[10px] font-medium", getStageBadgeClass(row.lead_stage))}>
                                                        {row.lead_stage}
                                                    </span>
                                                ) : "—"}
                                                {row.needs_reassignment && (
                                                    <span
                                                        className="px-1.5 py-0.5 rounded-full text-[9px] font-medium bg-amber-100 text-amber-800 border border-amber-300 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700/50"
                                                        title={`Marked Unanswered ${row.unanswered_count || 5}+ times — reassign suggested`}
                                                    >
                                                        ⚠ Reassign
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-3 py-2.5 max-w-[180px]">
                                            {(() => {
                                                const log = row.status_log?.[0];
                                                if (!log) return <span className="text-gray-300 dark:text-gray-600">—</span>;
                                                return (
                                                    <div>
                                                        <div className="flex items-center gap-1 text-[10px] text-gray-600 dark:text-gray-400">
                                                            <span className="font-medium">{log.from_status || '—'}</span>
                                                            <ArrowRight className="h-2.5 w-2.5 text-gray-400 flex-shrink-0" />
                                                            <span className="font-semibold text-indigo-600 dark:text-indigo-400">{log.to_status || '—'}</span>
                                                        </div>
                                                        {log.remarks && (
                                                            <p className="text-[10px] text-gray-400 dark:text-gray-500 truncate max-w-[160px] mt-0.5" title={log.remarks}>
                                                                {log.remarks}
                                                            </p>
                                                        )}
                                                    </div>
                                                );
                                            })()}
                                        </td>
                                        <td className="px-3 py-2.5">
                                            {(() => {
                                                const log = row.status_log?.[0];
                                                const dateStr = log?.next_followup_date;
                                                const s = getFollowUpStatus(dateStr);
                                                if (!s) return <span className="text-gray-300 dark:text-gray-600">—</span>;
                                                return (
                                                    <div>
                                                        <span className={cn(
                                                            "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold",
                                                            s.type === 'overdue'  && "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
                                                            s.type === 'today'    && "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
                                                            s.type === 'upcoming' && "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
                                                        )}>
                                                            {s.type === 'overdue'  && <AlertCircle className="h-2.5 w-2.5" />}
                                                            {s.type === 'today'    && <CheckCircle  className="h-2.5 w-2.5" />}
                                                            {s.type === 'upcoming' && <Clock        className="h-2.5 w-2.5" />}
                                                            {s.label}
                                                        </span>
                                                        <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">
                                                            {dayjs(dateStr).format("DD MMM YYYY hh:mm A")}
                                                        </p>
                                                    </div>
                                                );
                                            })()}
                                        </td>
                                        {sessionStorageRole !== 'third_party' && (
                                            <td className="px-3 py-2.5 text-right" onClick={e => e.stopPropagation()}>
                                                <div className="flex items-center justify-end gap-1">
                                                    <button
                                                        onClick={() => openEditDialog(row)}
                                                        className="p-1.5 rounded text-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors"
                                                        title="Edit"
                                                    >
                                                        <Edit className="h-3.5 w-3.5" />
                                                    </button>
                                                    <button
                                                        onClick={() => { setSelectedRow(row); setShowAssigneeLog(true); }}
                                                        className="p-1.5 rounded text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                                        title="Assignee Log"
                                                    >
                                                        <Logs className="h-3.5 w-3.5" />
                                                    </button>
                                                    {(row?.is_draft || sessionStorageRole === "super_admin") && (
                                                        <button
                                                            onClick={() => { setSelectedRow(row); setIsDeleteConfirmOpen(true); }}
                                                            className="p-1.5 rounded text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                                            title="Delete"
                                                        >
                                                            <Trash className="h-3.5 w-3.5" />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* ── pagination footer ── */}
                    <div className="flex-shrink-0 px-4 py-2.5 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between bg-gray-50 dark:bg-gray-800/50 flex-wrap gap-2">
                        <div className="flex items-center gap-2">
                            <span className="text-[10px] text-gray-500 dark:text-gray-400">Rows per page</span>
                            <select value={pageSize} onChange={e => handlePageSizeChange(Number(e.target.value))}
                                className="text-[10px] border border-gray-200 dark:border-gray-700 rounded px-1.5 py-0.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none"
                            >
                                {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                            </select>
                            <span className="text-[10px] text-gray-400 dark:text-gray-500">
                                {total === 0 ? '0' : `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, total)}`} of {total}
                            </span>
                        </div>
                        <div className="flex items-center gap-1">
                            <button onClick={() => handlePageChange(1)} disabled={page === 1} className="px-1.5 py-0.5 text-[10px] rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300">«</button>
                            <button onClick={() => handlePageChange(Math.max(1, page - 1))} disabled={page === 1} className="p-0.5 rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"><ChevronLeft className="h-3 w-3" /></button>
                            {Array.from({ length: totalPages }, (_, i) => i + 1)
                                .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                                .reduce((acc, p, i, arr) => { if (i > 0 && p - arr[i - 1] > 1) acc.push('…'); acc.push(p); return acc; }, [])
                                .map((p, i) => p === '…'
                                    ? <span key={`e${i}`} className="px-1 text-[10px] text-gray-400">…</span>
                                    : <button key={p} onClick={() => handlePageChange(p)} className={cn("min-w-[22px] px-1.5 py-0.5 text-[10px] rounded border transition-colors", p === page ? "bg-indigo-600 text-white border-indigo-600" : "border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700")}>{p}</button>
                                )
                            }
                            <button onClick={() => handlePageChange(Math.min(totalPages, page + 1))} disabled={page === totalPages} className="p-0.5 rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"><ChevronRight className="h-3 w-3" /></button>
                            <button onClick={() => handlePageChange(totalPages)} disabled={page === totalPages} className="px-1.5 py-0.5 text-[10px] rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300">»</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Assign modal ── */}
            <DialogModal
                open={showAssigneeLogForUser}
                onClose={() => { setShowAssigneeLogForUser(false); setSelectedRows([]); }}
                onSave={handleAssignedToUser}
                title={`Assign ${selectedRows.length} lead${selectedRows.length === 1 ? '' : 's'}`}
                subtitle="To one person, or shared across a team in turn"
                closeLabel="Cancel"
                saveLabel="Assign"
                loading={saving}
                width="500px"
            >
                <AssignForm value={assignForm} onChange={setAssignForm} users={assignMeta.users}
                    teams={assignMeta.teams} requireReason={assignMeta.requireReason} />
            </DialogModal>

            {/* ── Add / Edit modal ── */}
            <DialogModal
                open={openDialog}
                onClose={resetForm}
                onDraft={handleDraft}
                onSave={handleSave}
                title={editMode
                    ? (defaultCategory === 'channel_partner' ? "Edit Channel Partner" : "Edit Customer")
                    : (defaultCategory === 'channel_partner' ? "Add Channel Partner" : "Add New Customer")
                }
                closeLabel="Cancel"
                saveLabel={editMode ? "Update" : "Save"}
                draftLabel="Save as Draft"
                editMode={editMode}
                loading={saving}
                width="600px"
            >
                <form className="space-y-4 h-[calc(100vh-250px)] overflow-auto p-2">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Customer Name <span className="text-red-500">*</span></label>
                            <Input value={formData.customer_name} onChange={e => setFormData({ ...formData, customer_name: e.target.value })}
                                placeholder="Enter customer name" disabled={saving}
                                className="w-full rounded-lg border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20" />
                        </div>
                        <div className="space-y-2">
                            <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Customer Type <span className="text-red-500">*</span></label>
                            <Select size="large" value={formData.customer_type || undefined}
                                onChange={v => {
                                    const isCp = allCustomerTypes.find(t => t.name === v)?.is_channel_partner || false;
                                    setFormData({ ...formData, customer_type: v, lead_stage: isCp ? 'Onboarding' : 'New Lead' });
                                }}
                                placeholder="Select customer type" options={customerTypeOptions} className="w-full" disabled={saving} />
                        </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="space-y-2">
                            <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Lead Source</label>
                            <Select size="large" value={formData.lead_source || undefined}
                                onChange={v => setFormData({ ...formData, lead_source: v,
                                    // Picking another source drops the partner with it, matching
                                    // what the API would have done on save anyway. Leaving it set
                                    // and invisible is how a lead ends up filed under Instagram
                                    // while still counting towards a partner's leaderboard row.
                                    sourced_by_partner: isPartnerSource(v) ? formData.sourced_by_partner : null })}
                                placeholder="Select lead source" options={leadSourceOptions} className="w-full" disabled={saving} />
                        </div>
                        <div className="space-y-2">
                            <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                Lead Stage
                                {isFormTypeCP && <span className="ml-1.5 text-[10px] text-purple-600 font-normal">(CP)</span>}
                            </label>
                            <Select size="large" value={formData.lead_stage || undefined} onChange={v => setFormData({ ...formData, lead_stage: v })}
                                placeholder="Select stage"
                                options={formStageOptions.map(s => ({ value: s, label: s }))}
                                className="w-full" disabled={saving} />
                        </div>
                        <div className="space-y-2">
                            <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Assigned To</label>
                            <Select size="large" value={formData.assigned_to || undefined} onChange={v => setFormData({ ...formData, assigned_to: v })}
                                placeholder="Select user" options={UserList.map(u => ({ value: u.id, label: u.label, emp_code: u.emp_code, role_type: u.role_type, email: u.email }))}
                                className="w-full" disabled={saving} showSearch
                                filterOption={(input, opt) => opt.label.toLowerCase().includes(input.toLowerCase())}
                                optionRender={(opt) => (
                                    <div className="flex flex-col">
                                        <span className="font-medium">{opt.data.label}</span>
                                        <div className="flex gap-2 text-xs text-gray-500">
                                            <span>{opt.data.emp_code}</span><span>•</span>
                                            <span className="capitalize">{opt.data.role_type?.replace('_', ' ')}</span><span>•</span>
                                            <span>{opt.data.email}</span>
                                        </div>
                                    </div>
                                )} />
                        </div>
                    </div>
                    {isPartnerSource(formData.lead_source) && (
                        <div className="space-y-2 p-3 rounded-lg border border-purple-200 dark:border-purple-800/50 bg-purple-50/50 dark:bg-purple-900/10">
                            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
                                <Handshake className="h-3.5 w-3.5 text-purple-600" />
                                Sourced By <span className="text-red-500">*</span>
                                <span className="text-[10px] font-normal text-gray-500">Which channel partner sent this lead</span>
                            </label>
                            <Select size="large" showSearch value={formData.sourced_by_partner || undefined}
                                onChange={v => setFormData({ ...formData, sourced_by_partner: v })}
                                placeholder="Select channel partner" className="w-full" disabled={saving}
                                options={partnerOptions.map(p => ({
                                    value: p.id,
                                    label: p.customer_name,
                                    city: p.city,
                                    stage: p.lead_stage,
                                    sourced: p.sourced_count,
                                }))}
                                filterOption={(input, opt) => (opt.label || '').toLowerCase().includes(input.toLowerCase())}
                                optionRender={(opt) => (
                                    <div className="flex flex-col">
                                        <span className="font-medium">{opt.data.label}</span>
                                        <div className="flex gap-2 text-xs text-gray-500">
                                            {opt.data.city && <><span>{opt.data.city}</span><span>•</span></>}
                                            <span>{opt.data.stage || '—'}</span><span>•</span>
                                            <span>{opt.data.sourced || 0} leads</span>
                                        </div>
                                    </div>
                                )} />
                            {partnerOptions.length === 0 && (
                                <p className="text-[10px] text-amber-600 dark:text-amber-400">
                                    No channel partners on record yet — add one under Channel Partners first.
                                </p>
                            )}
                        </div>
                    )}
                    <div className="space-y-2">
                        <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Customer Description</label>
                        <Textarea value={formData.notes} onChange={e => setFormData({ ...formData, notes: e.target.value })}
                            placeholder="Enter customer description" rows={3} showCharCount maxLength={250} disabled={saving}
                            className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                    </div>
                    <div className="space-y-2">
                        <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Address</label>
                        <Textarea value={formData.address} onChange={e => setFormData({ ...formData, address: e.target.value })}
                            placeholder="Enter address" rows={3} disabled={saving}
                            className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                    </div>
                    <LocationSelects
                        value={{ country: formData.country, state: formData.state, city: formData.city }}
                        onChange={loc => setFormData({ ...formData, ...loc })}
                        disabled={saving} />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Contact Name</label>
                            <Input value={formData.contact_name} onChange={e => setFormData({ ...formData, contact_name: e.target.value })}
                                placeholder="Enter contact name" disabled={saving}
                                className="w-full rounded-lg border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                        </div>
                        <div className="space-y-2">
                            <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Contact Number</label>
                            <Input type="number" value={formData.contact_number} onChange={e => setFormData({ ...formData, contact_number: e.target.value })}
                                placeholder="Enter contact number" disabled={saving}
                                className="w-full rounded-lg border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                        </div>
                    </div>
                    {!editMode && (
                        <DuplicateWarning check={dupCheck} choice={dupChoice} reason={dupReason}
                            onReason={setDupReason} busy={saving}
                            onOpen={openExistingLead} onAttach={handleAttachEnquiry}
                            onCreateAnyway={() => setDupChoice(c => c === 'create' ? null : 'create')} />
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Preferred Language <span className="text-[10px] text-gray-500 font-normal">(AI Voice Agent)</span></label>
                            <Select size="large" value={formData.preferred_language}
                                onChange={v => setFormData({ ...formData, preferred_language: v })}
                                options={preferredLanguageOptions} className="w-full" disabled={saving} />
                        </div>
                        {projectAssignOn && (
                            <div className="space-y-2">
                                <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Project</label>
                                <Select size="large" showSearch
                                    // A new lead defaults to the project on screen; an
                                    // existing one shows where it actually is, even if nowhere.
                                    value={formData.project_id || (editMode ? undefined : activeProjectId) || undefined}
                                    onChange={v => setFormData({ ...formData, project_id: v })}
                                    placeholder={editMode ? "Not in any project" : "Select project"}
                                    options={projectOptions.map(p => ({ value: p.id, label: p.name }))}
                                    filterOption={(input, opt) => (opt.label || '').toLowerCase().includes(input.toLowerCase())}
                                    className="w-full" disabled={saving} />
                            </div>
                        )}
                    </div>
                    {projectAssignOn && (() => {
                        // The main project is the one above; a lead is not "also" in it.
                        const mainId = formData.project_id || (editMode ? null : activeProjectId);
                        return (
                            <div className="space-y-2">
                                <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                    Also in projects
                                    <span className="ml-1.5 text-[10px] text-gray-500 font-normal">Shows this lead in those projects' lists too</span>
                                </label>
                                <Select size="large" mode="multiple" allowClear showSearch
                                    value={(formData.extra_project_ids || []).filter(id => id !== mainId)}
                                    onChange={v => setFormData({ ...formData, extra_project_ids: v })}
                                    placeholder="None"
                                    options={[...projectOptions,
                                              ...(formData.extra_projects_known || []).filter(k => !projectOptions.some(p => p.id === k.id))]
                                        .filter(p => p.id !== mainId).map(p => ({ value: p.id, label: p.name }))}
                                    filterOption={(input, opt) => (opt.label || '').toLowerCase().includes(input.toLowerCase())}
                                    className="w-full" disabled={saving} />
                            </div>
                        );
                    })()}
                    <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                        <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Active Status</label>
                        <div className="flex items-center gap-3">
                            <span className={cn("text-xs font-medium", formData.is_active ? "text-green-600 dark:text-green-400" : "text-gray-500 dark:text-gray-400")}>
                                {formData.is_active ? "Active" : "Inactive"}
                            </span>
                            <button type="button" onClick={() => setFormData({ ...formData, is_active: !formData.is_active })}
                                className={cn("relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200",
                                    formData.is_active ? "bg-sidebar-primary" : "bg-gray-300 dark:bg-gray-600",
                                    saving ? "cursor-not-allowed opacity-50" : "cursor-pointer")}
                                disabled={saving}>
                                <span className={cn("inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform duration-200",
                                    formData.is_active ? "translate-x-4" : "translate-x-1")} />
                            </button>
                        </div>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400"><span className="text-red-500">*</span> indicates required field</p>
                </form>
            </DialogModal>

            {/* ── Delete confirm modal ── */}
            <ConfirmPopupModal
                open={isDeleteConfirmOpen}
                onClose={() => { setIsDeleteConfirmOpen(false); setSelectedRow(null); }}
                onConfirm={selectedRow?.is_draft ? handleDraftDelete : handleDelete}
                title="Delete Customer"
                description="Are you sure you want to delete this customer? This action cannot be undone."
                type="warning"
                confirmLabel="Delete"
                destructive={true}
                loading={saving}
            />

            {/* ── Owner history modal (how and why each owner change happened) ── */}
            <DialogModal
                open={showAssigneeLog}
                onClose={() => setShowAssigneeLog(false)}
                title={`Owner history${selectedRow?.customer_name ? ` — ${selectedRow.customer_name}` : ''}`}
                showActions={false}
                width="820px"
            >
                {selectedRow && <OwnerHistory leadId={selectedRow.id} />}
            </DialogModal>
        </ContainerCard>
    );
};

export default CustomersList;
